/**
 * DrawPriorityField — admin-only "draw score" control for one membership.
 *
 * Surfaces a member's exact ferris-wheel priority position for a single
 * organization and lets an admin/delegate override it. Per the draw spec
 * this number must NEVER be shown to contestants — it only lives here, on
 * the admin user-management page, and is loaded lazily (the priority list
 * is an internal tool). 1 = highest priority (first pick); the value is
 * clamped to 1..member-count since a position outside the list is invalid.
 */

import { useEffect, useState } from 'react';
import { Loader2, Save, Trophy, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  useMemberPriority,
  useSetMemberPriority,
} from '../../../api/extensions/usePriorityOps';
import { useToast } from '../../../contexts/ToastContext';

interface DrawPriorityFieldProps {
  associationId: string;
  userProfileId: string;
}

export default function DrawPriorityField({
  associationId,
  userProfileId,
}: DrawPriorityFieldProps) {
  // Lazy: nothing is fetched until the admin opens the control, so the
  // priority list isn't pulled for every membership row on page load.
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const { data, isLoading, isError, error } = useMemberPriority(
    associationId,
    userProfileId,
    { enabled: open },
  );
  const setPriority = useSetMemberPriority();
  const { showSuccess, showError } = useToast();

  // Seed the editable draft once the real position arrives.
  useEffect(() => {
    if (data?.priority_position != null) {
      setDraft(String(data.priority_position));
    }
  }, [data?.priority_position]);

  const total = data?.total_members ?? 0;
  // 404 = the user has no card on this association's ferris wheel
  // (e.g. a non-member). Non-members intentionally have no priority.
  const notOnList =
    isError && /not found|not on/i.test((error as Error)?.message ?? '');

  const parsed = Number.parseInt(draft, 10);
  const draftValid =
    Number.isFinite(parsed) && parsed >= 1 && total > 0 && parsed <= total;
  const dirty = data != null && parsed !== data.priority_position;

  const handleSave = async () => {
    if (!draftValid || !dirty) return;
    try {
      const res = await setPriority.mutateAsync({
        associationId,
        userProfileId,
        priority_position: parsed,
      });
      showSuccess(
        'Draw priority updated',
        `Now #${res.priority_position} of ${res.total_members}.`,
      );
    } catch (e: unknown) {
      showError(
        'Failed to update draw priority',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  if (!open) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs shrink-0"
            onClick={() => setOpen(true)}
          >
            <Trophy className="h-3.5 w-3.5 mr-1 text-amber-500" />
            Draw priority
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          Internal draw score (admin only — never shown to contestants)
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="flex items-center gap-1 shrink-0">
      <Trophy className="h-3.5 w-3.5 text-amber-500" />
      {isLoading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : notOnList ? (
        <span className="text-xs text-muted-foreground italic">
          not on priority list
        </span>
      ) : isError ? (
        <span className="text-xs text-red-600 dark:text-red-400">
          failed to load
        </span>
      ) : (
        <>
          <Input
            type="number"
            min={1}
            max={total || undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-8 w-[72px] text-xs"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draftValid && dirty) handleSave();
              if (e.key === 'Escape') setOpen(false);
            }}
            aria-label="Draw priority position"
          />
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            / {total}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                disabled={!draftValid || !dirty || setPriority.isPending}
                onClick={handleSave}
              >
                {setPriority.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Save (1 = first pick)</TooltipContent>
          </Tooltip>
        </>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setOpen(false)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Close</TooltipContent>
      </Tooltip>
    </div>
  );
}
